import type { Locale } from 'date-fns';
import { differenceInSeconds, format, isValid, parse } from 'date-fns';
import { isObject, isString } from 'lodash-es';

import constants from '../constants';
import * as log from '../logger';

const minute = 60;
const hour = minute * 60;
const day = hour * 24;
const week = day * 7;
const month = day * 30;
const year = day * 365;

export const seconds = {
  minute,
  hour,
  day,
  week,
  month,
  year,
} as const;

/**
 * Return plural form of the noun given, including the number
 * @param number - integer
 * @param noun - ex: 'policy'
 * @param pluralNoun? - ex: 'policies'
 * @return - ex: '1 policy' or '5 policies'
 */
export function pluralize(
  number: number | undefined | null,
  noun: string,
  pluralNoun = `${noun}s`,
): string {
  return number === 1 ? `${number} ${noun}` : `${number} ${pluralNoun}`;
}

/**
 * Return plural form of the noun given
 * @param number - integer
 * @param noun - ex: 'policy'
 * @param pluralNoun - Optional, ex: 'policies'
 * @return - ex: 'policy' or 'policies'
 */
export function pluralizeWord(
  number: number | undefined | null,
  noun: string,
  pluralNoun = `${noun}s`,
): string {
  return number === 1 ? noun : pluralNoun;
}

/**
 * Return plural form by given config
 * @param number - integer
 * @param one - ex: 'policy has an error'
 * @param many - ex: 'policies are with error'
 * @return one of above
 */
export function chooseFromPlurals(number: number, one: string, many: string): string {
  const absN = Math.abs(number);
  return absN === 1 ? one : many;
}

export function possessiveWord(word: string): string {
  return word.endsWith('s') ? `${word}'` : `${word}'s`;
}

/**
 * Given a number of secs, return a string like
 * '2 days, 2 hours' or '3 mins, 45 secs' or '45 secs'
 */
export function mixedTime(secs: number): string {
  if (secs > year) {
    return `${pluralize(Math.floor(secs / year), 'year')}, ${pluralize(Math.floor((secs % year) / month), 'month')}`;
  } else if (secs >= month) {
    return `${pluralize(Math.floor(secs / month), 'month')}, ${pluralize(Math.floor((secs % month) / day), 'day')}`;
  } else if (secs >= week) {
    return `${pluralize(Math.floor(secs / week), 'week')}, ${pluralize(Math.floor((secs % week) / day), 'day')}`;
  } else if (secs >= day) {
    return `${pluralize(Math.floor(secs / day), 'day')}, ${pluralize(Math.floor((secs % day) / hour), 'hour')}`;
  } else if (secs >= hour) {
    return `${pluralize(Math.floor(secs / hour), 'hour')}, ${pluralize(Math.floor((secs % hour) / minute), 'min')}`;
  } else if (secs >= minute) {
    return `${pluralize(Math.floor(secs / minute), 'min')}, ${pluralize(secs % minute, 'sec')}`;
  }
  return `${pluralize(secs, 'sec')}`;
}

interface AdaptiveDateFormatOptions {
  short?: boolean;
}

/**
 * Returns a formatted date string
 * that has increasing detail as more time passes
 *
 * Example results:
 * 9:21 AM
 * Tues, 9:21 AM
 * May 10, 9:21 AM
 * 5/10/2016, 9:21 AM
 *
 * @param dateInput - either an ISO8601 date string, i.e "2016-08-29T09:12:33.001Z"
 *                    or a Date object
 */
export function adaptiveDateFormat(
  dateInput: string | Date | object | undefined | null,
  options: AdaptiveDateFormatOptions = {},
): string {
  let date: Date | null = null;

  if (!dateInput) {
    // In our system, this is a valid empty date value
    return '';
  }
  if (dateInput instanceof Date) {
    date = dateInput;
  } else if (isString(dateInput) || isObject(dateInput)) {
    date = new Date(dateInput as string);
  } else {
    log.error('Invalid input to adaptiveDateFormat: not a String or Date object', dateInput);
    return '';
  }

  if (!isValid(date)) return '';

  const now = new Date();
  const diffInSeconds = differenceInSeconds(now, date);

  const sameDay = () =>
    now.getDate() === date!.getDate() &&
    now.getMonth() === date!.getMonth() &&
    now.getFullYear() === date!.getFullYear();

  // 5/10/2016, 9:21am
  if (now.getFullYear() !== date.getFullYear()) {
    if (options.short) return format(date, 'M/d/yy');
    return format(date, 'M/d/yy, h:mmaaa');
  }

  // Today, 9:21am
  if (sameDay()) {
    return format(date, "'Today', h:mmaaa");
  }

  // Tues, 9:21am
  if (!sameDay() && diffInSeconds < week) {
    if (options.short) return format(date, 'EEE haaa');
    return format(date, 'EEE, h:mmaaa');
  }

  // May 10, 9:21am
  if (options.short) return format(date, 'MMM d haaa');
  return format(date, 'MMM d, h:mmaaa');
}

/**
 * Returns the number of milliseconds until midnight
 */
export function millisUntilMidnight(): number {
  const midnight = new Date();
  midnight.setHours(24);
  midnight.setMinutes(0);
  midnight.setSeconds(0);
  midnight.setMilliseconds(0);
  return midnight.getTime() - new Date().getTime();
}

/**
 * Displays the number with its ordinal suffix
 *
 * Example: "1st", "2nd", "3rd", "4th"
 * nth(23) -> '23rd'
 */
export function nth(value: number): string {
  const onesPlace = Math.floor(value % 10);
  switch (onesPlace) {
    case 1:
      return `${value}st`;
    case 2:
      return `${value}nd`;
    case 3:
      return `${value}rd`;
    default:
      return `${value}th`;
  }
}

/**
 * Recursively find and push all error messages to the target array
 */
export const recursiveSearch = (object: Record<string, any>, array: string[]): void => {
  const oneErrorKeys = Object.keys(object);
  oneErrorKeys.forEach((key) => {
    if (key === 'message') {
      array.push(object.message);
    } else if (key === 'cause') {
      const subPhases = object.fields?.find(
        (f: { name?: string }) => f.name == constants.FAILED_SUB_PHASES,
      );
      if (subPhases) return;
      recursiveSearch(object.cause, array);
    } else if (key === 'fields') {
      const subPhases = object.fields?.find(
        (f: { name?: string }) => f.name == constants.FAILED_SUB_PHASES,
      );
      if (!subPhases) return;
      subPhases.value?.forEach((err: { Err?: Record<string, any> }) => {
        if (!err.Err) return;
        try {
          recursiveSearch(err.Err, array);
        } catch (e) {
          log.warn('Failed to parse JSON in error.fields.FailedSubPhases.Err', e);
        }
      });
    }
  });
};

/**
 * Display time difference in mixedTime format, i.e. '1 min, 1 sec'
 * @param value - The time value to calculate the difference from.
 * @param anotherTime - Optional time value to calculate the difference to. Defaults to the current time.
 * @returns The time difference in mixedTime format.
 */
export function timeDiff(
  value: string | number | undefined | null,
  anotherTime?: string | number,
): string {
  if (!value || value === 0) return '';
  const date = new Date(value as string | number);
  if (!isValid(date)) return '';
  const otherDate = anotherTime ? new Date(anotherTime) : new Date();
  return mixedTime(Math.abs(differenceInSeconds(date, otherDate)));
}

/**
 * Returns the suffix for the day of the month
 * @param day The day of the month
 * @returns The suffix for the day of the month
 */
export const getDaySuffix = (day: number): string => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

/**
 * Converts a date time from the incoming format to a long date time string in the format of 'EEEE, MMMM d, yyyy, h:mm:ss a'
 * @param options dateTime is the string you want to format, incomingFormat is the format of the string you are passing in (default is 'yyyy-MM-dd HH:mm:ss'), locale is the locale you want to use for formatting (default is 'en-US')
 * @returns The long date time string in the format of 'EEEE, MMMM d, yyyy, h:mm:ss a'
 */
// Pre-register all date-fns locale files so Vite can emit each one as its own
// lazy chunk. Using import.meta.glob avoids the vite:dynamic-import-vars warning
// that occurs when a bare package specifier contains a dynamic path segment —
// date-fns has an exports map, so Vite can't statically resolve
// `date-fns/locale/${locale}` against it. The glob works directly on the file
// system and sidesteps the exports-map check entirely.
// Exclude cdn.js and cdn.min.js — those are date-fns CDN distribution bundles
// (~625 KB each) that contain every locale combined. They are not individual
// locale files and should never be loaded at runtime.
const dateFnsLocales = import.meta.glob<{ default: Locale }>([
  '/node_modules/date-fns/locale/*.js',
  '!/node_modules/date-fns/locale/cdn*.js',
]);

interface LongDateTimeOptions {
  dateTime: string;
  incomingFormat?: string;
  locale?: string;
}

export const toLongDateTime = async ({
  dateTime,
  incomingFormat = 'yyyy-MM-dd HH:mm:ss',
  locale = 'en-US',
}: LongDateTimeOptions): Promise<string> => {
  let localeModule!: { default: Locale };

  try {
    const key = `/node_modules/date-fns/locale/${locale}.js`;
    if (!dateFnsLocales[key]) throw new Error(`Locale not found: ${locale}`);
    localeModule = await dateFnsLocales[key]();
  } catch (e) {
    log.error(`Failed to load locale module for locale ${locale}, defaulting to enUS`, e);
    localeModule = await dateFnsLocales['/node_modules/date-fns/locale/en-US.js']();
  }

  const parsedDate = parse(dateTime, incomingFormat, new Date(), {
    locale: localeModule.default,
  });

  const day = parsedDate.getDate();
  const daySuffix = getDaySuffix(day);

  const formattedDate = format(parsedDate, `EEEE, MMMM d'${daySuffix}' yyyy h:mm a`, {
    locale: localeModule.default,
  });

  return formattedDate;
};
